/* eslint-disable no-unsafe-optional-chaining */
import React, { useState, useEffect } from 'react';
import {
  string,
  shape,
  func,
} from 'prop-types';
import { CountDown } from 'components/common';
import {
  CampaignLayoutB,
  Loading,
  mediaQueries,
  Banner,
  sizes,
  COLORS,
} from '@hyphengroup/cag-components';
import styled from 'styled-components';
import { Query, Mutation } from 'react-apollo';
import Layout from 'components/layout';
import { withTracking } from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import { getStaticText, getImages, getAuthQueryVariables } from 'helpers/Utils';
import ErrorPage from 'pages/ErrorPage';
import { staticTextKeys as newsletterPanelStaticTextKeys } from 'components/staticPage/NewsLetter/NewsLetterPanel';

import GetTWCCCampaignPage from 'money101/overrides/queries/getTWCCCampaignPage.gql';
import GetTWPLCampaignPage from 'money101/overrides/queries/getTWPLCampaignPage.gql';
import { getTextBlockList, getImageAndLinkList } from 'money101/TWUtils';
import AddNewsletterSubscription from 'queries/newsletterSubscription.gql';
import { comparablesMappingHandler } from 'components/tracking/TrackingUtils';

const CampaignContainer = styled.div``;

const HeroBannerDesktop = styled.img`
  width: 100%;
  max-width: 100%;
  display: none;

  ${mediaQueries.small} {
    display: block;
  }
`;

const HeroBannerMobile = styled.img`
  width: 100%;
  max-width: 100%;

  ${mediaQueries.small} {
    display: none;
  }
`;

const campaignStaticTextKeys = [
  'REQUIRED_ERROR_MESSAGE',
  'CAMPAIGN_PRODUCT_TNC_TITLE',
  'CAMPAIGN_PRODUCT_LIST_BUTTON_TEXT',
  'CAMPAIGN_PRODUCT_LIST_BUTTON_NO_LINK_TEXT',
];

const TWCampaignPage = ({
  match: {
    params,
  },
  language,
  localisation,
  contentstackKeys,
  track,
  extraMethod,
}) => {
  const vertical = params?.vertical;
  const campaignName = params?.campaignName;
  const { imageAndLink, staticTextKeys } = contentstackKeys;
  const [email, setEmail] = useState('');
  const [subscribeResponse, setSubscribeResponse] = useState(null);

  useEffect(() => {
    track({ data: { location: 'campaign page' } });
  }, []);

  const executeMutation = (mutation) => {
    mutation({
      variables: {
        email,
        language: localisation[language],
        skipWelcome: false,
        source: 'WEB',
      },
    });
  };

  const onSuccessEvent = (data) => {
    setSubscribeResponse(data);
  };

  const onErrorEvent = (data) => {
    setSubscribeResponse(data);
  };

  const onClickProductEvent = (item) => {
    const data = {
      event: 'productClick',
      location: 'campaign page',
      ecommerce: {
        currencyCode: 'TWD',
        click:
        {
          actionField:
          {
            list: campaignName,
          },
          products: [
            {
              category: item.category,
              brand: item.brand,
              name: item.name,
              id: item.id,
              price: item.price,
              position: item.position,
              productDetails: item.productDetails,
              dealStatus: item.dealStatus,
              promoLabel: item.promoLabel,
              promotion: item.promotion,
            }],
        },
      },
    };
    if (item?.ctaButton?.link?.url) {
      const win = window.open(`/external?url=${item.ctaButton.link.url}`, '_blank');
      win.focus();
      track({ data });
    }
  };

  const handleEmailChange = ({ target: { value } }) => {
    setEmail(value);
  };

  return (
    <Query
      query={vertical === '信用卡' ? GetTWCCCampaignPage : GetTWPLCampaignPage}
      variables={{
        language: localisation[language],
        ...getAuthQueryVariables({
          contentstackKeys,
          initailStaticTextKeys: [
            ...imageAndLink.rewardSelection,
            ...staticTextKeys.rewardSelection,
            ...campaignStaticTextKeys,
            ...newsletterPanelStaticTextKeys,
          ],
          initialImages: [...imageAndLink.rewardSelection],
        }),
        productCCList: `${campaignName}_Campaign_Product_CC_List`,
        productPLList: `${campaignName}_Campaign_Product_PL_List`,
        // For separate CC and PL SEO data
        seoMetaData: `${campaignName}_${vertical === '信用卡' ? 'CC' : 'PL'}_Campaign_SEO_Metadata`,
        // For separate CC and PL Text Block List data
        countdown: `${campaignName}_${vertical === '信用卡' ? 'CC' : 'PL'}_Campaign_Countdown`,
        sequenceActions: `${campaignName}_${vertical === '信用卡' ? 'CC' : 'PL'}_Campaign_Sequence_Actions`,
        productHighlight: `${campaignName}_${vertical === '信用卡' ? 'CC' : 'PL'}_Campaign_Product_Highlight`,
        videoPanel: `${campaignName}_${vertical === '信用卡' ? 'CC' : 'PL'}_Campaign_Video_Panel`,
        // For separate CC and PL Image and Links data
        heroBannerImg: `${campaignName}_${vertical === '信用卡' ? 'CC' : 'PL'}_Campaign_Hero_Banner`,
        sequenceActionsImg: `${campaignName}_${vertical === '信用卡' ? 'CC' : 'PL'}_Campaign_Sequence_Actions`,
        termsAndConditions: `${campaignName}_${vertical === '信用卡' ? 'CC' : 'PL'}_Campaign_TNC`,
      }}
    >
      {({
        loading,
        data,
        error,
        refetch,
      }) => {
        if (loading) {
          return (<Loading />);
        }
        const seoMetadata = data?.seoMetadata;
        const { footer, header } = data?.pageConfig;
        const customerProfile = data?.customerProfile;
        const staticTextList = getStaticText(data);
        const imageAndLinkList = getImages(data);

        const countdownText = getTextBlockList('Countdown', data);
        const sequenceActionsText = getTextBlockList('SequenceActions', data);
        const productHighlightText = getTextBlockList('ProductHighlight', data);
        const videoPanelText = getTextBlockList('VideoPanel', data);

        const heroBannerImg = getImageAndLinkList('HeroBannerImg', data);
        const sequenceActionsImg = getImageAndLinkList('SequenceActionsImg', data);

        const verticalPLProducts = data?.ProductPLList?.result?.products.map((item, idx) => {
          const tmpData = {
            ...item,
            price: '',
            // eslint-disable-next-line no-underscore-dangle
            category: item.__typename,
            brand: item?.provider?.providerName,
            name: item.productName,
            id: item?.title,
            promotion: (item?.promotional?.type || []).map(t => t.key).join('|'),
            promoLabel: (item?.promotional?.type || []).map(t => t.display).join('|'),
            dealStatus: item?.ctaButton?.show ? 'true' : 'false',
            promotional: item?.promotional?.type[0],
            // Display on campaign product list
            title: productHighlightText[idx * 2 + 1],
            description: productHighlightText[idx * 2 + 2],
            productDetails: (item?.comparables || []).map(comparablesMappingHandler).join('|'),
            position: idx + 1,
            buttonText: item?.ctaButton?.link?.url
              ? staticTextList.CAMPAIGN_PRODUCT_LIST_BUTTON_TEXT
              : staticTextList.CAMPAIGN_PRODUCT_LIST_BUTTON_NO_LINK_TEXT,
            disabled: !item?.ctaButton?.link?.url,
          };

          return {
            ...tmpData,
            onClick: () => onClickProductEvent(tmpData),
          };
        }) || [];

        const verticalCCProducts = data?.ProductCCList?.result?.products.map((item, idx) => {
          const tmpData = {
            ...item,
            price: '',
            // eslint-disable-next-line no-underscore-dangle
            category: item.__typename,
            brand: item?.provider?.providerName,
            name: item.productName,
            id: item?.title,
            promotion: (item?.promotional?.type || []).map(t => t.key).join('|'),
            promoLabel: (item?.promotional?.type || []).map(t => t.display).join('|'),
            dealStatus: item?.ctaButton?.show ? 'true' : 'false',
            promotional: item?.promotional?.type[0],
            // Display on campaign product list
            title: productHighlightText[idx * 2 + 1],
            description: productHighlightText[idx * 2 + 2],
            productDetails: (item?.comparables || []).map(comparablesMappingHandler).join('|'),
            position: idx + 1,
            buttonText: item?.ctaButton?.link?.url
              ? staticTextList.CAMPAIGN_PRODUCT_LIST_BUTTON_TEXT
              : staticTextList.CAMPAIGN_PRODUCT_LIST_BUTTON_NO_LINK_TEXT,
            disabled: !item?.ctaButton?.link?.url,
          };
          return {
            ...tmpData,
            onClick: () => onClickProductEvent(tmpData),
          };
        }) || [];

        const newsLetterPanel = {
          ...data?.pageConfig?.subscribe,
          requiredText: staticTextList.REQUIRED_ERROR_MESSAGE,
          signUpErrorText: staticTextList.NEWSLETTER_SIGNUP_ERROR_MESSAGE,
          signUpValidationErrorText: staticTextList.NEWSLETTER_SIGNUP_VALIDATION_ERROR_MESSAGE,
          signUpSuccessText: staticTextList.NEWSLETTER_SIGNUP_SUCCESS_MESSAGE,
          extraMethod,
          value: email,
        };

        const sequenceActions = {
          background: '#f7fcff',
          heading: sequenceActionsText[0],
          section: sequenceActionsImg.map((item, idx) => ({
            title: sequenceActionsText[idx * 2 + 1],
            description: sequenceActionsText[idx * 2 + 2],
            image: {
              url: item?.image?.url,
              title: sequenceActionsText[idx * 2 + 1],
            },
          })),
        };

        const campaignProductList = {
          title: productHighlightText[0],
          productList: vertical === '信用卡' ? verticalCCProducts : verticalPLProducts,
          variant: verticalCCProducts.length > 1 || verticalPLProducts.length > 1 ? 'default' : 'center',
        };

        const videoPanel = {
          heading: videoPanelText[0],
          subheading: videoPanelText[1],
          link: videoPanelText[2],
        };

        const termsAndConditions = {
          dataSelectorId: `${campaignName}_campaign-tnc`,
          content: data?.termsAndConditions[0]?.text,
          title: staticTextList.CAMPAIGN_PRODUCT_TNC_TITLE,
        };

        return (
          <Layout
            footer={footer}
            header={header}
            seoMetadata={seoMetadata}
            customerProfile={customerProfile}
            staticText={staticTextList}
            images={imageAndLinkList}
            refetch={refetch}
            componentConfig={data?.componentConfig}
            isSticky
            variant="navBarSticky"
          >
            {!error
              && (
                <CampaignContainer>
                  {countdownText.length === 8 && (
                    <Banner
                      variant={countdownText[0]}
                      description={(
                        <CountDown
                          iconName={countdownText[1]}
                          name={countdownText[2]}
                          toTime={countdownText[3]}
                          timeOffset={8}
                          loadingText={countdownText[4]}
                          endText={countdownText[5]}
                          displayFormat={countdownText[6]}
                          maxUnit={countdownText[7]}
                          fontSize={['px14', 'px18', 'px18', 'px18']}
                          iconSize={['medium', 'large', 'large', 'large']}
                          variant="secondary"
                          weight="semiBold"
                          justifyContent={['left', 'center']}
                          marginLeft={['5px', sizes(1)]}
                          fontColor={(countdownText[0] === 'cloudy' || countdownText[0] === 'rosy') ? COLORS.TEXT_01 : COLORS.BRAND_01}
                        />
                      )}
                    />
                  )
                  }
                  {heroBannerImg[0]?.image?.url
                    && (
                      <>
                        <HeroBannerMobile alt="campaignHeroBanner_Mobile" src={heroBannerImg[0]?.image?.url} />
                        <HeroBannerDesktop alt="campaignHeroBanner" src={heroBannerImg[1]?.image?.url || heroBannerImg[0]?.image?.url} />
                      </>
                    )
                  }
                  <Mutation
                    mutation={AddNewsletterSubscription}
                    variables={{
                      email,
                      language: localisation[language],
                      skipWelcome: false,
                      source: 'WEB',
                    }}
                    onCompleted={onSuccessEvent}
                    onError={onErrorEvent}
                  >
                    {(mutation, {
                      data: mutationData,
                      loading: mutationLoading,
                      error: mutationError,
                    }) => (
                      <CampaignLayoutB
                        sequenceActions={sequenceActions}
                        campaignProductList={campaignProductList}
                        videoPanel={videoPanel}
                        termsAndConditions={termsAndConditions}
                        newsLetterPanel={{
                          ...newsLetterPanel,
                          loading: mutationLoading,
                          error: mutationError,
                          success: mutationData?.addNewsletterSubscription,
                          emailIsRequired: false,
                          handlers: {
                            handleEmailChange: (e) => handleEmailChange(e),
                            subscribeBtnClick: () => {
                              executeMutation(mutation);
                            },
                          },
                        }}
                        withoutHeaderAndFooter
                      />
                    )}
                  </Mutation>
                </CampaignContainer>
              )
            }
            {
              error && (<ErrorPage errorCode={404 || subscribeResponse} />)
            }
          </Layout>
        );
      }
      }
    </Query>
  );
};

TWCampaignPage.propTypes = {
  match: shape({}).isRequired,
  localisation: shape({}).isRequired,
  language: string.isRequired,
  contentstackKeys: shape({}),
  track: func.isRequired,
  extraMethod: shape({
    url: string.isRequired,
  }),
};

TWCampaignPage.defaultProps = {
  contentstackKeys: {},
  extraMethod: null,
};

export default withTracking(withApplicationConfig(TWCampaignPage, ['languages', 'contentstackKeys', 'newsletterPanel']));
